import { useCallback, useEffect, useMemo } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'

import { Breadcrumbs, Button, Card, Layout } from '@loadsmart/loadsmart-ui'

import { RequestStatus } from 'core/domain/Request'
import { AsyncContent } from 'core/ui/async-content/AsyncContent'
import { LoadingState } from 'core/ui/async-content/LoadingState'
import { Icon } from 'core/ui/components/icon/Icon'

import { useLoadSetup } from '../hooks/useLoadSetup'
import { LoadSummary } from '../load-summary/LoadSummary'
import { RelatedLoadsList } from '../related-load/RelatedLoadsList'
import { LoadSetup } from './LoadSetup'
import {
  BookingConfirmationWrapper,
  HeaderWrapper,
  LoadSetupWrapper,
  MainContent,
  RateconWrapper,
  ShipmentTitle,
  ShipmentWrapper,
  StyledCard,
  StyledCardBody,
  StyledCardTitle,
  TitleWrapper,
} from './LoadSetupPage.styles'

type LoadSetupPageParams = {
  loadUUID: string
}

type LoadSetupPageMatch = {
  params: LoadSetupPageParams
}

type LoadSetupPageProps = {
  match: LoadSetupPageMatch
}

export function LoadSetupPage({ match }: LoadSetupPageProps) {
  const history = useHistory()
  const {
    loadOffer,
    shipment,
    fetchShipmentStatus,
    fetchLoadOfferStatus,
    isFetchingRateConfirmation,
    fetchShipment,
    fetchLoadOfferByLoadId,
    fetchRateConfirmationByRefNumber,
  } = useLoadSetup()
  const loadUUID = match.params.loadUUID
  const shipmentUrl = `/shipments/${loadOffer?.refNumber}`
  const backToLoadboardURL = '/loads/'

  const handleBackToLoadboard = useCallback(() => {
    history.push(backToLoadboardURL)
  }, [backToLoadboardURL, history])

  const breadcrumbEntries = useMemo(
    () => [
      {
        label: 'Load board',
        onClick: handleBackToLoadboard,
      },
      { label: 'Load Setup' },
    ],
    [handleBackToLoadboard]
  )

  const onFetchLoadOfferByLoadId = useCallback(
    async (signal: AbortSignal) => {
      try {
        await fetchLoadOfferByLoadId(loadUUID, signal)
      } catch (error) {
        toast.error('This load is no longer available')

        handleBackToLoadboard()
      }
    },
    [handleBackToLoadboard, fetchLoadOfferByLoadId, loadUUID]
  )

  const handleViewRatecon = useCallback(async () => {
    try {
      const rateconFileUrl = await fetchRateConfirmationByRefNumber(loadOffer!.refNumber)

      window.open(rateconFileUrl, '_blank', 'noopener')
    } catch (error) {
      toast.error('Error downloading file')
    }
  }, [fetchRateConfirmationByRefNumber, loadOffer])

  const onFetchShipment = useCallback(
    async (refNumber: string, signal: AbortSignal) => {
      try {
        await fetchShipment(refNumber, signal)
      } catch (error) {
        toast.error('This load is no longer available')

        handleBackToLoadboard()
      }
    },
    [handleBackToLoadboard, fetchShipment]
  )

  useEffect(
    function onLoadOfferLoaded() {
      const isReady = fetchLoadOfferStatus === RequestStatus.Done
      if (isReady && loadOffer) {
        const controller = new AbortController()

        onFetchShipment(loadOffer.refNumber, controller.signal)

        return () => controller.abort()
      }
    },
    [fetchLoadOfferStatus, loadOffer, onFetchShipment]
  )

  useEffect(
    function onLoad() {
      const controller = new AbortController()

      onFetchLoadOfferByLoadId(controller.signal)

      return () => controller.abort()
    },
    [onFetchLoadOfferByLoadId]
  )

  useEffect(() => {
    const isLoadOfferReady = fetchLoadOfferStatus === RequestStatus.Done
    const isShipmentReady = fetchShipmentStatus === RequestStatus.Done

    if ((isLoadOfferReady && loadOffer?.activeOffer) || (isShipmentReady && !shipment)) {
      toast.error('This load is no longer available')
      handleBackToLoadboard()
    }
  }, [fetchLoadOfferStatus, fetchShipmentStatus, shipment, loadOffer, handleBackToLoadboard])

  return (
    <MainContent>
      <Layout.Stack space="m">
        <HeaderWrapper>
          <Card.Title>
            <TitleWrapper>
              <Breadcrumbs onBack={handleBackToLoadboard} entries={breadcrumbEntries} />
              {loadOffer && (
                <RateconWrapper>
                  <Button disabled={isFetchingRateConfirmation} onClick={handleViewRatecon}>
                    View ratecon
                  </Button>
                </RateconWrapper>
              )}
            </TitleWrapper>
          </Card.Title>
        </HeaderWrapper>
        <BookingConfirmationWrapper>
          <LoadSetupWrapper>
            <StyledCard>
              <Card.Title leading={<Icon name="board" size="18px" />}>
                Set up your load for a better experience
              </Card.Title>
              <StyledCardBody>
                <AsyncContent status={fetchShipmentStatus} loadingState={<LoadingState />}>
                  <LoadSetup loadOffer={loadOffer!} shipment={shipment!} />
                </AsyncContent>
              </StyledCardBody>
            </StyledCard>
          </LoadSetupWrapper>
          <ShipmentWrapper>
            <StyledCard>
              <StyledCardTitle leading={<Icon name="check" size="18px" />}>
                <ShipmentTitle>Shipment details</ShipmentTitle>
                {/* @ts-expect-error */}
                <Button as={Link} to={shipmentUrl} target="_blank">
                  See more
                </Button>
              </StyledCardTitle>
              <StyledCardBody>
                <AsyncContent status={fetchLoadOfferStatus} loadingState={<LoadingState />}>
                  <LoadSummary loadOffer={loadOffer!} />
                </AsyncContent>
              </StyledCardBody>
            </StyledCard>
          </ShipmentWrapper>
        </BookingConfirmationWrapper>
        <RelatedLoadsList loadUUID={loadUUID} context="LOAD_SETUP_RELATED_LOAD" />
      </Layout.Stack>
    </MainContent>
  )
}
